import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';

const useRecentsStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      minHeight: 0,
      flex: '1 1 auto',
      fontFamily: "'Nunito', sans-serif",
    },
    filterBar: {
      display: 'flex',
      flex: '0 0 auto',
      flexWrap: 'nowrap',
      alignItems: 'center',
      gap: 2,
      overflowX: 'auto',
      background: t.surface3,
      borderRadius: 10,
      padding: 3,
      alignSelf: 'flex-start',
      maxWidth: '100%',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    filterSegment: {
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      minHeight: 32,
      border: 'none',
      background: 'transparent',
      padding: '0 12px',
      borderRadius: 8,
      fontFamily: 'inherit',
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
      color: t.text2,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out, color 150ms ease-out',
      '&:hover': {
        color: t.text,
      },
      '&:focus-visible': {
        outline: `2px solid ${t.focus}`,
        outlineOffset: 1,
      },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    },
    filterSegmentActive: {
      background: t.surface,
      color: t.text,
      fontWeight: CS_WEIGHT.heavy,
      boxShadow: `0 0 0 1px ${t.controlLine}`,
    },
    filterCount: {
      color: t.text2,
      fontWeight: CS_WEIGHT.regular,
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      background: t.surface,
      borderRadius: 14,
      border: `1px solid ${t.line}`,
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      borderBottom: `1px solid ${t.line}`,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out',
      '&:last-child': {
        borderBottom: 'none',
      },
      '&:hover': {
        background: t.surface2,
      },
      '&:focus-visible': {
        outline: `2px solid ${t.focus}`,
        outlineOffset: -2,
      },
    },
    cardMain: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '14px 12px 14px 14px',
      minWidth: 0,
      '@media (max-width: 360px)': {
        gap: 8,
        padding: '12px 8px',
      },
    },
    body: {
      flex: '1 1 auto',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    primaryRow: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 6,
      minWidth: 0,
    },
    primaryLabel: {
      fontSize: 15,
      lineHeight: '20px',
      fontWeight: CS_WEIGHT.heavy,
      color: t.text,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
    primaryLabelMissed: {
      color: t.neg,
    },
    countBadge: {
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      color: t.text2,
      flex: '0 0 auto',
      fontVariantNumeric: 'tabular-nums',
    },
    countBadgeMissed: {
      color: t.neg,
    },
    trailingTime: {
      ...CS_FONT.caption,
      marginLeft: 'auto',
      paddingLeft: 8,
      flex: '0 0 auto',
      color: t.text2,
      whiteSpace: 'nowrap',
    },
    directionIcon: {
      flex: '0 0 auto',
      width: 16,
      height: 16,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: t.text2,
    },
    directionIconMissed: {
      color: t.neg,
    },
    secondaryRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      minWidth: 0,
    },
    secondaryMeta: {
      ...CS_FONT.small,
      color: t.text2,
      fontVariantNumeric: 'tabular-nums',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    summaryLine: {
      ...CS_FONT.small,
      color: t.text2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
    chipsRow: {
      marginTop: 4,
    },
    trailing: {
      flex: '0 0 auto',
      alignSelf: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
    },
    recallAction: {
      width: 44,
      height: 44,
      borderRadius: '50%',
      border: 'none',
      display: 'grid',
      placeItems: 'center',
      background: t.callSoft,
      color: t.callText,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out, color 150ms ease-out, transform 120ms ease-out',
      '&:hover': {
        background: t.call,
        color: t.callInk,
      },
      '&:active': {
        transform: 'scale(0.96)',
      },
      '&:focus-visible': {
        outline: `2px solid ${t.focus}`,
        outlineOffset: 2,
      },
      '&:disabled': {
        background: t.surface3,
        color: t.text3,
        cursor: 'not-allowed',
        transform: 'none',
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&:active': { transform: 'none' },
      },
    },
    pipelineRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginTop: 2,
      minWidth: 0,
    },
    pipelineActive: {
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.info,
      animation: '$pipelinePulse 1.4s ease-in-out infinite',
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    },
    pipelineMuted: {
      ...CS_FONT.caption,
      color: t.text2,
    },
    pipelineDone: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.pos,
    },
    pipelineError: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.neg,
    },
    pipelineRetry: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      minHeight: 28,
      border: `1px solid ${t.controlLine}`,
      background: t.surface,
      color: t.text,
      fontFamily: 'inherit',
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      borderRadius: 8,
      padding: '0 10px 0 8px',
      cursor: 'pointer',
      '& svg': { fontSize: 14 },
      '&:hover': {
        background: t.surface3,
      },
      '&:focus-visible': {
        outline: `2px solid ${t.focus}`,
        outlineOffset: 2,
      },
      '&:disabled': {
        color: t.text2,
        cursor: 'default',
      },
    },
    '@keyframes pipelinePulse': {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.55 },
      '100%': { opacity: 1 },
    },
    expandIcon: {
      color: t.text2,
      transition: 'transform 150ms ease-out',
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    },
    expandIconOpen: {
      transform: 'rotate(180deg)',
    },
    memberList: {
      display: 'flex',
      flexDirection: 'column',
      borderTop: `1px solid ${t.line}`,
      background: t.surface2,
      padding: '4px 0',
    },
    memberRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
      border: 'none',
      background: 'transparent',
      width: '100%',
      minHeight: 44,
      textAlign: 'left',
      padding: '6px 12px 6px 66px',
      cursor: 'pointer',
      fontFamily: 'inherit',
      minWidth: 0,
      '&:hover': {
        background: t.surface3,
      },
      '&:focus-visible': {
        outline: `2px solid ${t.focus}`,
        outlineOffset: -2,
      },
      '@media (max-width: 360px)': {
        paddingLeft: 16,
      },
    },
    memberTime: {
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      color: t.text,
      whiteSpace: 'nowrap',
    },
    memberDuration: {
      ...CS_FONT.small,
      color: t.text2,
      whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums',
    },
    memberSpacer: {
      flex: '1 1 auto',
    },
    memberStoppedLine: {
      ...CS_FONT.caption,
      color: t.text2,
      flex: '0 0 100%',
      minWidth: 0,
    },
    memberChevron: {
      color: t.text2,
      flex: '0 0 auto',
    },
    ownNote: {
      ...CS_FONT.caption,
      margin: 0,
      color: t.text2,
      padding: '0 4px',
    },
    loadingList: {
      display: 'flex',
      flexDirection: 'column',
      background: t.surface,
      borderRadius: 14,
      border: `1px solid ${t.line}`,
    },
    loadingRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '14px 12px 14px 14px',
      borderBottom: `1px solid ${t.line}`,
      '&:last-child': {
        borderBottom: 'none',
      },
      '& .MuiSkeleton-root': {
        background: t.surface3,
      },
      '@media (prefers-reduced-motion: reduce)': {
        '& .MuiSkeleton-root': { animation: 'none' },
      },
    },
    loadingBody: {
      flex: '1 1 auto',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    loadingChips: {
      display: 'flex',
      gap: 6,
      marginTop: 2,
    },
    stateButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      minHeight: 40,
      padding: '0 14px',
      borderRadius: 10,
      border: `1px solid ${t.controlLine}`,
      background: t.surface,
      color: t.text,
      fontFamily: 'inherit',
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      '& svg': { fontSize: 18 },
      '&:hover': { background: t.surface3 },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    },
  };
});

export default useRecentsStyles;
